import React from 'react';
import { Helmet } from 'react-helmet-async';

/**
 * Schema.org Structured Data Component for SEO
 * Provides JSON-LD markup for search engines
 */

// LocalBusiness Schema for the dental practice
export const LocalBusinessSchema = () => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Dentist",
    "@id": "https://fortworthdental.com/#dentist",
    "name": "Fort Worth Dental",
    "image": "https://fortworthdental.com/images/logo.png",
    "url": "https://fortworthdental.com",
    "telephone": "(817) 985-3337",
    "email": "recall@fortworthdental.com",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "4620 Citylake Blvd W",
      "addressLocality": "Fort Worth",
      "addressRegion": "TX",
      "postalCode": "76132",
      "addressCountry": "US"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": 32.7283609,
      "longitude": -97.4382597
    },
    "openingHoursSpecification": [
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": "Monday",
        "opens": "09:00",
        "closes": "17:00"
      },
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": "Tuesday",
        "opens": "09:00",
        "closes": "18:00"
      },
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": "Wednesday",
        "opens": "07:00",
        "closes": "17:00"
      },
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": "Thursday",
        "opens": "09:00",
        "closes": "18:00"
      },
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": "Friday",
        "opens": "07:00",
        "closes": "14:00"
      }
    ],
    "priceRange": "$$",
    "areaServed": [
      {
        "@type": "City",
        "name": "Fort Worth",
        "sameAs": "https://en.wikipedia.org/wiki/Fort_Worth,_Texas"
      },
      {
        "@type": "City",
        "name": "Aledo"
      },
      {
        "@type": "City",
        "name": "Benbrook"
      },
      {
        "@type": "City",
        "name": "Stephenville"
      }
    ],
    "sameAs": [
      "https://www.facebook.com/FWDental",
      "https://x.com/FortWorthTeeth",
      "https://www.youtube.com/channel/UCUFwAVnty9mZPxUYT9VLyIA",
      "https://www.yelp.com/biz/fort-worth-dental-fort-worth",
      "https://www.linkedin.com/company/fort-worth-dental/"
    ],
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "reviewCount": "500",
      "bestRating": "5"
    },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Dental Services",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Dental Implants"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Invisalign"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "LANAP Laser Gum Treatment"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Cosmetic Dentistry"
          }
        }
      ]
    }
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

// Service Schema for individual service pages
export const ServiceSchema = ({ 
  serviceName, 
  description, 
  url,
  image,
  priceRange = "$$",
  areaServed = ["Fort Worth", "Aledo", "Benbrook", "TX"]
}) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "MedicalProcedure",
    "name": serviceName,
    "description": description,
    "url": url,
    "image": image,
    "procedureType": "http://schema.org/NoninvasiveProcedure",
    "provider": {
      "@type": "Dentist",
      "name": "Fort Worth Dental",
      "telephone": "(817) 985-3337",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "4620 Citylake Blvd W",
        "addressLocality": "Fort Worth",
        "addressRegion": "TX",
        "postalCode": "76132"
      }
    },
    "areaServed": areaServed.map(area => ({
      "@type": "City",
      "name": area
    }))
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

// FAQ Schema for pages with FAQs
export const FAQSchema = ({ faqs }) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

// BreadcrumbList Schema
export const BreadcrumbSchema = ({ items }) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": item.url
    }))
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

// Professional (Doctor) Schema
export const DoctorSchema = ({ 
  name, 
  title, 
  description, 
  image,
  specialties = []
}) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Dentist",
    "name": name,
    "jobTitle": title,
    "description": description,
    "image": image,
    "worksFor": {
      "@type": "Dentist",
      "name": "Fort Worth Dental",
      "url": "https://fortworthdental.com"
    },
    "medicalSpecialty": specialties.length > 0 ? specialties : ["Dentistry"],
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "4620 Citylake Blvd W",
      "addressLocality": "Fort Worth",
      "addressRegion": "TX",
      "postalCode": "76132"
    }
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

// Review/Testimonial Schema
export const ReviewSchema = ({ reviews }) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Dentist",
    "name": "Fort Worth Dental",
    "review": reviews.map(review => ({
      "@type": "Review",
      "author": {
        "@type": "Person",
        "name": review.author
      },
      "reviewRating": {
        "@type": "Rating",
        "ratingValue": review.rating,
        "bestRating": "5"
      },
      "reviewBody": review.text
    }))
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

// WebSite Schema for search box
export const WebsiteSchema = () => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "Fort Worth Dental",
    "url": "https://fortworthdental.com",
    "potentialAction": {
      "@type": "SearchAction",
      "target": "https://fortworthdental.com/search?q={search_term_string}",
      "query-input": "required name=search_term_string"
    }
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

export default {
  LocalBusinessSchema,
  ServiceSchema,
  FAQSchema,
  BreadcrumbSchema,
  DoctorSchema,
  ReviewSchema,
  WebsiteSchema
};
